<template>
  <!-- Main view when no family selected -->
  <template v-if="!selectedFamily">
    <div class="grid grid-cols-2 gap-5">
      <ReportCard v-if="productsReport" :title="$t('report.families_view')">
        <FamiliesInsights
          :products-report="productsReport"
          :center="center"
          :report-id="srId"
          @navigate-to-family="selectFamily"
        />
      </ReportCard>

      <ReportCard :title="$t('report.sales_distribution.distribution_title')">
        <FamiliesDoughnutChart
          v-if="productsReport"
          :products-report="productsReport"
          :center-id="centerId"
        />
        <div v-else class="flex h-32 justify-center items-center">
          <p class="text-muted-foreground">{{ $t("report.sales_distribution.no_data") }}</p>
        </div>
      </ReportCard>
    </div>
  </template>

  <!-- Drill-down into family detail -->
  <template v-else>
    <FamilyDetailInline
      :family-name="selectedFamily"
      :products-report="productsReport"
      :center-id="centerId"
      @back="selectedFamily = null"
    />
  </template>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, type Ref } from 'vue'
import { useReportContext } from '../../../composables/useReportContext'
import { useAppSettingsStore } from '../../../stores/AppSettingsStore'
import ReportCard from '../../ReportCard.vue'
import FamiliesInsights from '../../FamiliesInsights.vue'
import FamiliesDoughnutChart from '../../report-sections/FamiliesDoughnutChart.vue'
import FamilyDetailInline from './FamilyDetailInline.vue'
import type { Center } from '../../../user'

const { card, centerId, srId } = useReportContext()
const appSettingsStore = useAppSettingsStore()

const center = computed<Center>(() => {
  const c = appSettingsStore.getCenter(centerId)
  if (!c) {
    throw new Error('FamiliesTab: center not found for centerId=' + centerId)
  }
  return c
})

const productsReport = computed(() => {
  return card.value?.productsReport ?? null
})

const selectedFamily = ref<string | null>(null)

// Cross-tab navigation: if SalesTab requests drill-down into a family,
// ReportDesktopCard provides the family name via inject
const pendingFamily = inject<Ref<string | null>>('pendingFamily', ref(null))

const selectFamily = (familyName: string) => {
  selectedFamily.value = familyName
}

onMounted(() => {
  if (pendingFamily.value) {
    selectedFamily.value = pendingFamily.value
    pendingFamily.value = null
  }
})
</script>
